/**
 * Client-side navigation search: modules and pages, from module config.
 *
 * Runs instantly on every keystroke, ahead of the debounced server-side entity
 * search. Matching is delegated to the shared navigation search engine
 * (`shared/utils/moduleSearch`), so a query ranks modules and pages here exactly
 * as it does in the module picker.
 */
import React from 'react';

import { i18n } from '../../../i18n';
import { type ModuleConfig } from '../../config/modules.config';
import { resolveModuleDescriptionKey, resolveModuleNameKey } from '../../hooks/useModuleName';
import { matchModulePages, matchModules } from '../../utils/moduleSearch';
import { isNavItemVisible, type NavItemAccess } from '../../utils/navItemAccess';

/** A navigable module or page result. */
export interface NavSearchResult {
  title: string;
  subtitle: string;
  iconNode: React.ReactNode;
  url: string;
  moduleId: string;
}

/** Module and page matches for one query, each ranked most-relevant-first. */
export interface NavSearchResults {
  modules: NavSearchResult[];
  pages: NavSearchResult[];
}

/**
 * Persona-aware module display name. When the forwarder persona is on,
 * Shipments reads as "Freight Operations" (see useModuleName). Plain function —
 * callers thread the resolved persona flag.
 */
function moduleName(module: ModuleConfig, sellsFreightServices: boolean): string {
  const nameKey = resolveModuleNameKey(module, sellsFreightServices);
  return i18n.exists(nameKey) ? i18n.t(nameKey) : module.id;
}

/** Persona-aware module description — the twin of {@link moduleName}. */
function moduleDescription(module: ModuleConfig, sellsFreightServices: boolean): string {
  const descriptionKey = resolveModuleDescriptionKey(module, sellsFreightServices);
  return i18n.exists(descriptionKey) ? i18n.t(descriptionKey) : '';
}

/**
 * Resolve an i18n key to its display string, falling back to the humanised last
 * segment of the key so a page is still findable (and readable) when its
 * translation is missing.
 */
function translateKey(key: string): string {
  if (i18n.exists(key)) return i18n.t(key);
  const lastSegment = key.split('.').pop() ?? key;
  return lastSegment.replace(/_/g, ' ');
}

/** Restrict candidate modules to the active module filter (`['all']` = no filter). */
function applyModuleFilter(modules: ModuleConfig[], moduleFilter: string[]): ModuleConfig[] {
  if (moduleFilter.length === 0 || moduleFilter.includes('all')) return modules;
  return modules.filter((m) => moduleFilter.includes(m.id));
}

/**
 * Search modules and their navigation pages.
 *
 * Access is gated at two levels: MODULE level by the caller (it passes only the
 * modules the viewer may open), and ITEM level here — a nav item carrying its
 * own `requiresAdmin` / `requiresSysadmin` inside an otherwise-open module is
 * filtered with the same predicate the sidebar uses, so the two surfaces agree
 * about what a given user can see.
 *
 * @param modules - candidate modules, already filtered to what the user may
 *   open (the module picker's enabled + pinned modules)
 * @param moduleFilter - the search bar's module filter, or `['all']`
 * @param access - the viewer's nav-item access (roles, connections, persona).
 *   Its `sellsFreightServices` flag also renders the Shipments module name as
 *   "Freight Operations".
 */
export function searchNavigation(
  modules: ModuleConfig[],
  query: string,
  moduleFilter: string[],
  access: NavItemAccess,
): NavSearchResults {
  if (!query) return { modules: [], pages: [] };

  const candidates = applyModuleFilter(modules, moduleFilter);
  const getModuleName = (module: ModuleConfig) => moduleName(module, access.sellsFreightServices);

  const { modules: matchedModules } = matchModules(candidates, query, getModuleName);
  const matchedModuleIds = new Set(matchedModules.map((m) => m.id));
  const matchedPages = matchModulePages(
    candidates,
    query,
    matchedModuleIds,
    getModuleName,
    translateKey,
    (navItem) => isNavItemVisible(navItem, access),
  );

  return {
    modules: matchedModules.map((module) => ({
      title: getModuleName(module),
      // The module's own description — tells the user what they'd be opening,
      // where a page result uses this line for its parent module instead.
      subtitle: moduleDescription(module, access.sellsFreightServices),
      iconNode: module.icon,
      url: module.defaultPath,
      moduleId: module.id,
    })),
    pages: matchedPages.map(({ module, navItem }) => ({
      title: translateKey(navItem.labelKey),
      subtitle: getModuleName(module),
      iconNode: navItem.icon,
      url: navItem.path,
      moduleId: module.id,
    })),
  };
}

/**
 * Get suggested pages for the empty search state.
 * Returns the first few nav items from each visible module (up to a limit).
 *
 * Gated exactly like {@link searchNavigation}: the caller supplies the modules
 * the viewer may open, and each nav item is filtered by the shared visibility
 * predicate — the resting state must not offer an admin-only page either.
 *
 * @param modules - candidate modules (same list as {@link searchNavigation})
 * @param access - the viewer's nav-item access (roles, connections, persona).
 *   Its `sellsFreightServices` flag also renders the Shipments subtitle as
 *   "Freight Operations".
 */
export function getSuggestedPages(
  modules: ModuleConfig[],
  moduleFilter: string[],
  access: NavItemAccess,
  limit = 6,
): NavSearchResult[] {
  const results: NavSearchResult[] = [];

  for (const module of applyModuleFilter(modules, moduleFilter)) {
    for (const navItem of module.navItems) {
      if (results.length >= limit) break;
      if (!isNavItemVisible(navItem, access)) continue;

      results.push({
        title: translateKey(navItem.labelKey),
        subtitle: moduleName(module, access.sellsFreightServices),
        iconNode: navItem.icon,
        url: navItem.path,
        moduleId: module.id,
      });
    }

    if (results.length >= limit) break;
  }

  return results;
}
